import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UnsubscribeBaseModel } from '../../../../base/model/UnsubscribeBase.model';
import { UserRightAssignmentRequestService } from '../../../../services/request/user-right-assignment-request.service';
import { CompoApp } from '../../../../services/CompoApp';
import { combineLatest, finalize, takeUntil } from 'rxjs';
import { UserRightsRoleModel } from '../../../../models/rights/user-rights-role/user-rights-role.model';
import { RequestClientService } from '../../../../services/client/request-client.service';
import { delay } from 'rxjs/operators';
import { AuthserviceService } from '../login-page/authservice.service';
import { AuthPageCommonComponent } from '../auth-page-common.component';
import { NgClass } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonComponent } from '../../../globals/button/button/button.component';
import { TranslateModule } from '@ngx-translate/core';
import { getAccessMode } from '../login-page/authentication-action.service';

@Component({
  selector: 'cp-select-role-page',
  templateUrl: './select-role-page.component.html',
  styleUrls: ['./select-role-page.component.scss', '../auth-page-common.scss'],
  imports: [AuthPageCommonComponent, NgClass, TooltipModule, SkeletonModule, ButtonComponent, TranslateModule]
})
export class SelectRolePageComponent extends UnsubscribeBaseModel implements OnInit {

  //region READONLY FIELDS
  readonly DEFAULT_SKELETON_COUNT: number = 3;
  //endregion

  //region FIELDS
  skeletons: any[];
  roles: UserRightsRoleModel[];
  selectedRoleId: string;
  selectableRoleIds: string[];
  inProgress: boolean = false;
  lang: string;
  //endregion

  //region CONSTRUCTOR
  constructor(
    private route: ActivatedRoute,
    private userRightsAssignmentRequestService: UserRightAssignmentRequestService,
    private app: CompoApp,
    private requestClientService: RequestClientService,
    private router: Router,
    private authService: AuthserviceService
  ) {
    super();
  }

  //endregion

  //region LIFECYCLE
  ngOnInit(): void {
    this.lang = this.app.data.getLang();

    this.app.on.langChange
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => {
        this.lang = this.app.data.getLang();
      });

    const roleCount: number = +this.route.snapshot.queryParamMap.get('roleCount');

    if (isNaN(roleCount) || !roleCount || roleCount < 2) {
      this.skeletons = new Array(this.DEFAULT_SKELETON_COUNT);
    } else {
      this.skeletons = new Array(roleCount);
    }

    const startDate: Date = new Date();

    combineLatest([
      this.userRightsAssignmentRequestService.getAllUserRightsRolesOfUser(this.app.data.getUser().id),
      this.userRightsAssignmentRequestService.getDefaultRole()
    ])
      .pipe(
        takeUntil(this.unSubscribe$),
        delay(this.requestClientService.calcDefaultTimeoutOf(startDate))
      )
      .subscribe(([roles, defaultRoleId]: ([UserRightsRoleModel[], string])) => {
        this.roles = roles;
        this.selectedRoleId = defaultRoleId;
        this.selectableRoleIds = this.roles.map(role => role.id);
      });
  }

  //endregion

  //region HOST LISTENERS
  @HostListener('window:keyup', ['$event'])
  onEnter(event) {
    const index = this.selectableRoleIds.indexOf(this.selectedRoleId);
    switch (event.code) {
      case 'ArrowUp':
        this.onSelectRole(this.selectableRoleIds[(this.selectableRoleIds.length + index - 1) % this.selectableRoleIds.length]);
        break;
      case 'ArrowDown':
        this.onSelectRole(this.selectableRoleIds[(index + 1) % this.selectableRoleIds.length]);
        break;
      case 'Enter':
        this.onSave();
        break;
      default:
        return;
    }
  }

  //endregion

  //region METHODS
  onSelectRole(roleId: string): void {
    if (this.inProgress) {
      return;
    }

    this.selectedRoleId = roleId;
  }

  onSave(): void {
    if (this.inProgress || !this.selectedRoleId) {
      return;
    }

    this.inProgress = true;

    this.userRightsAssignmentRequestService
      .changeLoggedInUserRightsRole(this.selectedRoleId)
      .pipe(
        takeUntil(this.unSubscribe$),
        finalize(() => this.inProgress = false)
      )
      .subscribe(() => {
        this.authService.initAccessModeFromRoleId(this.selectedRoleId);
        this.app.on.accessModeChange.next(getAccessMode(this.selectedRoleId));
        // this.headerClientService.userId(this.app.data.getUser()?.id);
        const pendingUrl = this.app.data.getPendingUrl();
        this.app.data.setPendingUrl(null);
        this.app.on.loginTrigger.next(this.app.data.getUser());
        this.router.navigate([pendingUrl || '/home']);
      });
  }

  //endregion
  protected roleItemTrackBy(v: any, idx: number) {
    return 'select-role-page-item-' + idx;
  }
}
